import React, { useEffect, useMemo } from "react";
import { Container, Typography, Button, Box } from "@mui/material";
import "./Pricing.css";
import { useDispatch, useSelector } from "react-redux";
import { fetchPlans } from "../../store/productDataSlice";
import { openContactModal } from "../../store/modalSlice";
import { t } from "../../utils/i18n";
import ButtonSys from "../../common/ButtonSys";

const Pricing = () => {
  const lang = useSelector((state) => state.i18n.lang);
  const dispatch = useDispatch();
  const productCode = useSelector((state) => state.products.product_code);
  const plansState = useSelector((state) => state.productData.plans);

  const handleOpenModal = (planTitle) => {
    dispatch(openContactModal(planTitle));
  };
  // features come embedded with plans now (via /plans-with-features)

  useEffect(() => {
    if (productCode) {
      if (plansState.status === "idle") dispatch(fetchPlans(productCode));
    }
  }, [dispatch, productCode, plansState.status]);

  const plans = useMemo(() => {
    const raw = Array.isArray(plansState.data) ? plansState.data : [];
    return raw.map((p) => ({
      title: (lang === "ar" ? p?.name_ar : p?.name) || "",
      price: p.price || "",
      allFeatureNames: Array.isArray(p.features)
        ? p?.features
            ?.map((f) => (lang === "ar" ? f?.name_ar : f?.name))
            .filter(Boolean)
        : [],
      enabledFeatureNames: new Set(
        Array.isArray(p.features)
          ? p.features
              .filter((f) => `${f?.value}` === "1")
              .map((f) => (lang === "ar" ? f?.name_ar : f?.name))
              .filter(Boolean)
          : []
      ),
    }));
  }, [plansState.data, lang]);

  const features = useMemo(() => {
    const derived = Array.from(
      new Set(plans.flatMap((p) => p.allFeatureNames || []))
    );
    if (derived.length > 0) return derived;
    return [];
  }, [plans]);

  if (!plans || plans.length === 0) {
    return null; 
  }

  return (
    <div id="pricing" className="pricing-section">
      <Box
        className="main-section"
        sx={{
          px: "30px !important",
          width: "90%",
          mx: "auto",
          mb: -1,
          bgcolor: (theme) => theme.palette.background.reviews,
        }}
      >
        <Typography
          variant="h6"
          fontWeight={500}
          mb={1}
          className="reviews-title"
        >
          {t(lang, "sections.features")}
        </Typography>
        <Typography variant="h2">{t(lang, "pricing.subtitle")}</Typography>

        <Box
          className="pricing-table-container"
          bgcolor={(theme) => theme.palette.background.pricingBg}
        >
          <Box
            sx={{
              maxHeight: 500,
              "&::-webkit-scrollbar": {
                width: "10px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#fff",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#000000ff",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "#1d1c1cff",
              },
            }}
          >
            <div className="pricing-table">
              {/* Feature Column */}
              <div className="feature-column">
                {Array.isArray(features) &&
                  features.map((feature, index) => (
                    <div key={index} className="feature-row">
                      <Typography variant="h5" fontWeight={400}>
                        {feature}
                      </Typography>
                    </div>
                  ))}
              </div>

              {/* Plan Columns */}
              <Box display={"flex"} alignItems={"center"} width={"70%"}>
                {plans.map((plan, index) => (
                  console.log("plans",plans),
                  <Box
                    key={index}
                    className="plan-column"
                    pb={3}
                    bgcolor={(theme) => theme.palette.background.planeColumn}
                    sx={{
                      borderRightWidth: "1px",
                      borderRightStyle: "solid",
                      borderRightColor: (theme) =>
                        theme.palette.background.planeHeaderBorder,
                    }}
                  >
                    <Box
                      className="plan-header"
                      bgcolor={(theme) => theme.palette.background.planeHeader}
                      sx={{
                        borderBottomWidth: "1px",
                        borderBottomStyle: "solid",
                        borderBottomColor: (theme) =>
                          theme.palette.background.planeHeaderBorder,
                      }}
                    >
                      <Typography variant="h5" fontWeight={600}>
                        {plan.title}
                      </Typography>
                    </Box>
                    <div className="plan-body">
                      {Array.isArray(features) &&
                        features.map((feature, idx) => (
                          <div key={idx} className="feature-cell">
                            <span
                              className={`icon ${
                                plan.enabledFeatureNames?.has(feature)
                                  ? "check"
                                  : "cross"
                              }`}
                            >
                              {plan.enabledFeatureNames?.has(feature) ? (
                                <svg
                                  width="25"
                                  height="20"
                                  viewBox="0 0 72 50"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M58.5002 0.726703C68.8502 -0.58712 74.9702 10.4919 68.2002 17.7505C57.6302 26.4162 48.1402 37.9704 37.5002 46.3565C32.7002 50.1396 26.6102 51.1925 20.8902 48.3878C17.8202 46.8783 5.61018 35.3986 3.01018 32.4542C-5.84982 22.4281 7.64019 9.9328 18.1102 17.7412C19.9302 19.0923 25.8102 25.5962 27.2802 25.8478C28.1602 25.9969 28.8102 25.6707 29.4302 25.1024C37.5302 18.6823 44.9402 9.11291 53.0902 3.03764C54.6302 1.89154 56.5202 0.978287 58.5002 0.726703Z"
                                    fill="#DD3740"
                                  />
                                </svg>
                              ) : (
                                <svg
                                  width="25"
                                  height="20"
                                  viewBox="0 0 36 34"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M6.8008 1.07378C12.1708 0.291078 14.4308 3.96255 17.8608 6.88837C19.9308 5.55591 21.4408 3.18894 23.6508 2.00557C29.1608 -0.948202 35.6408 2.89146 35.1108 8.77106C34.7808 12.4609 30.9808 14.3898 28.6208 16.9615C30.4708 18.9649 33.3908 20.7352 34.5108 23.2138C37.5808 30.0065 28.9308 36.0531 22.8908 31.5526C22.1208 30.9749 18.3408 27.0806 18.0008 27.0247C15.3108 28.8044 13.5608 32.0931 10.1408 32.8292C4.87083 33.9753 0.230811 30.1364 0.750811 25.142C1.11081 21.6198 4.9308 19.3928 7.2408 16.9515C4.1908 13.7741 0.16083 11.9292 0.77083 7.00939C1.11083 4.27925 3.88081 1.5024 6.81081 1.07378H6.8008Z"
                                    fill="white"
                                  />
                                </svg>
                              )}
                            </span>
                          </div>
                        ))}
                    </div>
                    <Box
                      display={"flex"}
                      flexDirection={"row"}
                      justifyContent={"center"}
                      my={2}
                    >
                      <ButtonSys
                        onClick={() => handleOpenModal(plan.title)}
                        text={t(lang, "pricing.request_a_quote")}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </div>
          </Box>
        </Box>
      </Box>
    </div>
  );
};

export default Pricing;
